import {
    Avatar,
    Badge,
    Box,
    Collapse,
    createStyles,
    Fab,
    Fade,
    Grid,
    LinearProgress,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import { Check, FastForwardRounded, PlayArrowRounded, ReplayRounded } from "@material-ui/icons";
import classNames from "classnames";
import { debounce } from "lodash";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { getGlobalUserId } from "shared/src/framework/network/headers";
import { Id } from "shared/src/models/framework/db";
import { Mode, PollComment, PollOption, PollTypeEnumString } from "shared/src/models/poll";
import { UserInfo } from "shared/src/models/user/user-info";
import { getComments } from "shared/src/network/requests/poll/poll-req";
import { PollCard } from "shared/src/network/requests/poll/poll-req-models";
import { userGet, userUpdate } from "shared/src/network/requests/user/user-req";
import { Analytic } from "src/analytic/analytic";
import { HEIGHT_LOWER_THRESHOLD } from "../const";
import { setCommentDrawerVisibility } from "../redux/app/AppSlice";
import { setMutedAudio, setOptionVideoIndex } from "../redux/polls/PollsSlice";
import { RootState } from "../redux/store";
import { colors } from "../theme";
import { getMediaBase64, getMediaPath } from "../utils/media";
import { navigatorShare } from "../utils/share";
import CommentDrawer from "./CommentDrawer";
import { MarqueeSlider } from "./MarqueeSlider/MarqueeSlider";
import ShareButton from "./ShareButton";

const styles = (theme: Theme) =>
    createStyles({
        questionContainer: {
            width: `calc(100% + ${theme.spacing(2)})`,
            height: `calc(100% + ${theme.spacing(1)})`,
            marginLeft: theme.spacing(-1),
            marginBottom: theme.spacing(-1),
            position: "relative",
            overflow: "hidden",
            wordBreak: "break-word",
        },
        statusImage: {
            top: "-20%",
            right: "-8%",
            width: "50%",
            opacity: "30%",
            position: "absolute",
        },
        optionLabel: {
            position: "absolute",
            zIndex: 2,
            padding: "2px 4px",
            borderRadius: 0,
            lineHeight: 0,
            borderBottomRightRadius: "4px",
            "& .MuiTypography-root": {
                lineHeight: 1,
            },
        },
        optionMedia: {
            objectFit: "cover",
            width: "100%",
            height: "100%",
        },
        optionResultBg: {
            position: "absolute",
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(255, 105, 105, 0.55)",
        },
        optionResultValueContainer: {
            position: "absolute",
            width: "50px",
            textAlign: "center",
            top: "calc(50% - 15px)",
            left: "calc(50% - 25px)",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            color: "white",
            borderRadius: 4,
            padding: "4px",
            opacity: 0,
            transition: "opacity 0.5s ease",
        },
        optionResultValueContainerFill: {
            opacity: 1,
        },
        optionPaper: {
            position: "relative",
            width: "100%",
            boxShadow: "-3px -3px 4px 2px rgb(255 255 255 / 60%), 3px 3px 4px 2px rgb(0 0 0 / 40%)",
        },
        optionContent: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
        },
        textContainer: {
            flexDirection: "column",
            "&.only-text": {
                height: "120px",
            },
            "&.has-media": {
                position: "absolute",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "flex-end",
                paddingBottom: "8px",
            },
        },
        boxShadowNone: {
            boxShadow: "none",
        },
        answeredPollOption: {
            boxShadow: "none",
        },
        selectedOption: {
            boxShadow: `0 0 0 2px rgb(242, 75, 75)`,
        },
        textWithMedia: {
            color: "white",
            padding: `0 ${theme.spacing(1)}`,
        },
        gradient: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: "absolute",
            background: "linear-gradient(transparent, 80%, rgba(0,0,0,0.75))",
        },
        progressBar: {
            position: "absolute",
            width: "80%",
            bottom: 4,
            zIndex: 1,
            "& .MuiLinearProgress-root": {
                height: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.grey[500],
                "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                    backgroundColor: "white",
                },
            },
        },
        playButtonDiv: {
            position: "absolute",
            fontSize: "24px",
            bottom: 0,
            left: 0,
            zIndex: 1,
            backgroundColor: "transparent",
            color: "white",
        },
        playButton: {
            position: "absolute",
            fontSize: "24px",
            bottom: 5,
            left: 8,
            zIndex: 1,
            backgroundColor: theme.palette.grey[500],
            borderRadius: "24px",
            color: "white",
        },
        videoButton: {
            position: "absolute",
            bottom: "8px",
            left: "8px",
            zIndex: 2,
        },
        videoOptionOneByOnePlayProgressBar: {
            bottom: 26,
        },
        selectedVideoOption: {
            bottom: 24,
        },
        bottomAction: {
            background: "transparent",
            padding: "5px 5px 0 5px",
            width: "80px",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        bottomActionImg: {
            height: "24px",
            marginBottom: "2px",
        },
        tickerArrow: {
            height: "10px",
        },
        tickerItem: {
            display: "flex",
            alignItems: "center",
            marginTop: "4px",
            marginBottom: "4px",
            marginRight: "30px",
            fontSize: "14px",
        },
    });

type OwnProps = {
    poll: Id<PollCard>;
    hasVideoEnded?: boolean;
    isPollFocused?: boolean;
    availableHeight?: number;
    availableWidth?: number;
    variant?: "normal" | "share";
    onOptionSelected?: (optionId: string) => void;
    mode?: Mode;
    showUserGuide?: boolean;
    onTrackingChange?: (checked: boolean) => void;
    onVideoButtonClick?: () => void;
    isTracking?: boolean;

    onClick?: () => void;
    linkParam?: string;
};

const mapState = (state: RootState, props: OwnProps) => ({
    optionVideoIndex: state.polls.optionVideoIndex[props.poll.id] || 0,
    mutedAudio: state.polls.mutedAudio,
    activeBottomNavigation: state.app.activeBottomNavigation,
    showInterestDialog: state.app.showInterestDialog,
    commentDrawerVisibility: state.app.commentDrawerVisibility,
});

const mapDispatch = {
    setCommentDrawerVisibility,
    setOptionVideoIndex,
    setMutedAudio,
};

const connector = connect(mapState, mapDispatch);

type Props = WithStyles<typeof styles> & ConnectedProps<typeof connector> & OwnProps;

type State = {
    selectedCategories: Array<string>;
    toggleRenderer: boolean;
    showCommentDrawer: boolean;
    commentList: Array<PollComment>;
};

class Component extends React.Component<Props, State> {
    optionLabel = ["A", "B", "C", "D"];
    shareTemplate: React.RefObject<HTMLDivElement>;
    videoRefs: Array<React.RefObject<HTMLVideoElement>>;
    options: Array<PollOption>;
    isDataUrl: boolean;
    currentUser?: string;
    user?: UserInfo = {};
    newImage: { [x: string]: string } = {};
    // To manage async setState operations
    mounted = false;
    constructor(props: Props) {
        super(props);
        const { poll } = props;
        this.shareTemplate = React.createRef();
        this.videoRefs = poll.options.map(() => React.createRef());
        this.state = {
            selectedCategories: [],
            toggleRenderer: false,
            showCommentDrawer: false,
            commentList: [],
        };
        this.options = poll.options;
        this.isDataUrl = false;
    }

    componentDidMount = async () => {
        this.mounted = true;
        const { poll, isPollFocused, variant, optionVideoIndex } = this.props;
        this.options = poll.options;
        const comments = await getComments({ pollId: this.props.poll.id });
        this.currentUser = getGlobalUserId();
        this.user = (await userGet({})).user;
        this.setState({ selectedCategories: this.user?.categoryIds || [], commentList: comments });
        if (variant !== "share") {
            if (isPollFocused) {
                const manageVideo = (video: React.RefObject<HTMLVideoElement>) => {
                    const current = video.current;
                    if (!current) return;
                    current.load();
                    this.tryPlayingVideo(current);
                };
                if (poll.videoPlayOption === "OneByOne") {
                    setTimeout(() => {
                        manageVideo(this.videoRefs[optionVideoIndex]);
                    }, 2000);
                } else if (poll.videoPlayOption === "SameTime") {
                    this.videoRefs.forEach(manageVideo);
                }
            }
        } else if (variant === "share") {
            this.changeImageToBase64();
        }
    };

    componentDidUpdate = async (prevProps: Props) => {
        const {
            isPollFocused,
            variant,
            poll,
            optionVideoIndex,
            activeBottomNavigation,
            commentDrawerVisibility,
            showInterestDialog,
            showUserGuide,
        } = this.props;
        this.options = poll.options;
        if (variant !== "share") {
            if (
                prevProps.isPollFocused !== isPollFocused ||
                prevProps.activeBottomNavigation !== activeBottomNavigation ||
                prevProps.commentDrawerVisibility !== commentDrawerVisibility ||
                prevProps.showInterestDialog !== showInterestDialog
            ) {
                this.videoRefs[prevProps.optionVideoIndex].current?.pause();
                const manageVideo = (video: React.RefObject<HTMLVideoElement>) => {
                    if (!video.current) return;
                    if (
                        isPollFocused &&
                        activeBottomNavigation === "polls" &&
                        !commentDrawerVisibility &&
                        !showInterestDialog &&
                        !poll.myVote
                    ) {
                        video.current.load();
                        const current = video.current;

                        this.tryPlayingVideo(current);
                    } else {
                        video.current.pause();
                        video.current.currentTime = 0;
                    }
                };
                if (poll.videoPlayOption === "OneByOne") {
                    setTimeout(() => {
                        manageVideo(this.videoRefs[optionVideoIndex]);
                    }, 2000);
                } else if (poll.videoPlayOption === "SameTime") {
                    this.videoRefs.forEach(manageVideo);
                }
            }
            if (prevProps.optionVideoIndex !== optionVideoIndex) {
                const video = this.videoRefs[optionVideoIndex].current;
                if (isPollFocused && video) {
                    video.currentTime = 0;
                    this.tryPlayingVideo(video);
                }
            }

            if (isPollFocused && prevProps.showUserGuide === true && showUserGuide !== true) {
                const video = this.videoRefs[optionVideoIndex].current;
                if (!!video)
                    setTimeout(() => {
                        this.tryPlayingVideo(video);
                    }, 500);
            }
        } else if (variant === "share" && prevProps.poll.id !== poll.id) {
            this.changeImageToBase64();
        }
    };

    changeImageToBase64 = async () => {
        for (let i = 0; i < this.options.length; i++) {
            const image = this.options[i].image;
            if (!!image && image.length > 0) {
                for (let j = 0; j < image.length; j++) {
                    const dataUrl = await getMediaBase64(image[j]);
                    this.newImage[image[j]] = dataUrl;
                }
            }
        }
        this.isDataUrl = true;
        this.handleTimeUpdate();
    };

    handleCommentClick = () => {
        this.setState({ showCommentDrawer: true });
        this.props.setCommentDrawerVisibility(true);
    };

    handleCommentDrawerClose = (updatedComments: Array<PollComment>) => {
        this.setState({ showCommentDrawer: false, commentList: updatedComments });
        this.props.setCommentDrawerVisibility(false);
    };

    handleShareClick = debounce(
        async () => {
            const { poll, linkParam, onClick } = this.props;
            let files: Array<Blob> = [];
            if (this.shareTemplate.current) {
                // request of S3 content from dom-to-image failing due to cors as image is already
                // loaded without cors header and cached. Cache bust will force fresh request with cors header.
                const imageBlob = await DomToImage.toBlob(this.shareTemplate.current, {
                    cacheBust: true,
                });
                files = [new File([imageBlob], "flashmob.png", { type: imageBlob.type })];
            }

            navigatorShare(poll, files, linkParam);
            onClick?.();
        },
        300,
        { leading: true, trailing: false }
    );

    tryPlayingVideo = (video: HTMLVideoElement, number?: number) => {
        const { poll, setMutedAudio, showUserGuide, optionVideoIndex, isPollFocused } = this.props;
        if (
            !!isPollFocused &&
            !showUserGuide &&
            !poll.myVote &&
            (this.videoRefs[optionVideoIndex].current?.paused ||
                poll.videoPlayOption !== "OneByOne")
        ) {
            video
                .play()
                .then(() => {
                    Analytic.addEvent({
                        event: "option_video_played",
                        question: JSON.stringify(poll.questionText),
                        pollType: poll?.pollType || "",
                        optionIndex: optionVideoIndex + 1,
                        defaultGTMConfig: "false",
                    });
                })
                .catch((error) => {
                    if (poll.videoPlayOption === "OneByOne") {
                        if (error.name === "NotAllowedError") {
                            setMutedAudio(true);
                            video.play();
                        } else {
                            video.load();
                        }
                    }
                });
        }
    };

    handleOptionClick = async (event: React.MouseEvent<HTMLElement>) => {
        const { optionVideoIndex, poll } = this.props;
        const optionId = event.currentTarget.dataset.optionId;

        if (optionId && poll.pollType === "follow") {
            const { selectedCategories } = this.state;
            let categoryListUpdated = false;
            let arr = selectedCategories;
            if (selectedCategories.includes(optionId)) {
                arr = selectedCategories.filter((category) => category !== optionId);
                categoryListUpdated = true;
            } else if (optionId != null) {
                arr.push(optionId);
                categoryListUpdated = true;
            }
            if (categoryListUpdated) {
                this.setState({
                    selectedCategories: arr,
                });
                await userUpdate({ categoryIds: arr });
            }
        } else {
            if (!!optionId) {
                this.videoRefs[optionVideoIndex].current?.pause();
                this.props.onOptionSelected?.(optionId);
            }
        }
    };

    handleErrorOnVideo = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        event.currentTarget.load();
    };

    handleLoadedData = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        const { poll, isPollFocused, optionVideoIndex } = this.props;
        if (
            poll.videoPlayOption === "OneByOne" &&
            this.videoRefs[optionVideoIndex].current !== event.currentTarget
        ) {
            return;
        }
        if (isPollFocused && event.currentTarget.paused) {
            const currentTarget = event.currentTarget;
            setTimeout(() => {
                this.tryPlayingVideo(currentTarget);
            }, 2000);
        }
    };

    handlePlayClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { poll, setOptionVideoIndex, optionVideoIndex } = this.props;
        event.stopPropagation();
        if (!!this.videoRefs[optionVideoIndex] && !!this.videoRefs[optionVideoIndex].current) {
            const current = this.videoRefs[optionVideoIndex].current;
            if (!!current && !current.paused) {
                current.currentTime = 0;
                current?.pause();
            }
            const optionIndex = event.currentTarget.dataset.index
                ? parseInt(event.currentTarget.dataset.index)
                : 0;
            const video = this.videoRefs[optionIndex].current;
            if (!!video) this.tryPlayingVideo(video);
            setOptionVideoIndex({
                index: optionIndex % poll.options.length,
                pollId: poll.id,
            });
        }
    };

    handleVideoEnd = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        const { poll, setOptionVideoIndex, optionVideoIndex } = this.props;
        if (poll.videoPlayOption === "OneByOne") {
            let newIndex = optionVideoIndex;
            let foundIndex = false;
            if (newIndex + 1 === poll.options.length) {
                this.videoRefs[optionVideoIndex].current?.pause();
            } else {
                while (!foundIndex) {
                    newIndex = (newIndex + 1) % poll.options.length;
                    const videoOption = poll.options[newIndex].video;
                    if (!!videoOption && videoOption.length > 0) {
                        foundIndex = true;
                    }
                }
                setOptionVideoIndex({
                    index: newIndex,
                    pollId: poll.id,
                });
            }
        }
    };

    handleTimeUpdate = () => {
        this.setState((state) => ({ toggleRenderer: !state.toggleRenderer }));
    };

    getDataUrl(img: any) {
        // Create canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        // Set width and height
        canvas.width = img.width;
        canvas.height = img.height;
        // Draw the image
        if (!!ctx) {
            ctx.drawImage(img, 0, 0);
            return canvas.toDataURL("image/jpeg");
        }
        return img;
    }

    render = () => {
        const {
            classes,
            poll,
            hasVideoEnded,
            availableHeight,
            availableWidth,
            mode,
            variant,
            mutedAudio,
            optionVideoIndex,
            onTrackingChange,
            onVideoButtonClick,
            isTracking,
        } = this.props;
        const { selectedCategories, commentList } = this.state;
        const isVideo = poll.pollType === "video" ? true : false;

        const tickerArrow = isVideo
            ? "/images/black-ticker-arrow-9badfb13b58364b5766e22329cf4b74f.webp"
            : "/images/white-ticker-arrow-ac59ac36708b5fd13a5a84d8300c9a90.webp";
        const chatter = isVideo
            ? "/images/white-comment-95fdab7b4cb95c45a240c629e3c08aae.webp"
            : "/images/blue-comment-4c250dfe2fcecb604e3072778612a152.webp";
        const track = isTracking
            ? isVideo
                ? "/images/white-track-enabled-80740ac7785448d212a97fdb1e945dec.webp"
                : "/images/blue-track-enabled-813e99fa16a0436ab5859cde9aa56e5e.webp"
            : isVideo
            ? "/images/white-track-disabled-aa013683cc5ff9436d6328b7d44e1d46.webp"
            : "/images/blue-track-disabled-54c0377ba538ef2ccd25f9de2bf86450.webp";
        const share = isVideo
            ? "/images/white-share-5c862e40dd2ba67d38b148d4bcbca121.webp"
            : "/images/blue-share-d21b596038aadcbdb23f6ad900a67ad6.webp";

        const totalVotes = poll.options.reduce<number>(
            (previousValue, each) => previousValue + (each.totalVote || 0),
            0
        );
        const selectedOptionId = poll.myVote?.optionId;
        const reduceSize =
            availableHeight !== undefined ? availableHeight < HEIGHT_LOWER_THRESHOLD : false;
        const requiredWidth = ((availableHeight || 0) - 170) / 1.23;
        let hGap;
        if (availableWidth && requiredWidth < availableWidth) {
            hGap = (availableWidth - requiredWidth) / 2;
        }
        let pollQuestionTotalLength = 0;
        poll.questionText.map((each) => {
            pollQuestionTotalLength += each.length;
            return pollQuestionTotalLength;
        });
        if (pollQuestionTotalLength > 50) {
            hGap = (hGap || 0) + 9;
        }
        const isShareVariant = variant === "share";
        let pollType = poll.pollType;
        const isVoted = !!poll.myVote;
        if (isVoted && mode !== "YOURPOLL" && pollType !== "follow") {
            hGap = (hGap || 0) + 16;
        }
        if (!pollType) {
            if (
                !Boolean(
                    poll.options.filter(
                        (each) => Boolean(each.image?.length) || Boolean(each.video?.length)
                    ).length
                )
            ) {
                pollType = PollTypeEnumString.text;
            }
        }
        return (
            <>
                <Grid
                    container
                    direction="column"
                    className={classNames(classes.questionContainer)}
                    sx={{ backgroundColor: isVideo ? "transparent" : "#f5f5f5" }}
                    id={"btn-ans-card-" + poll.id}
                    data-event={"result_option_click"}
                    data-question={JSON.stringify(poll.questionText)}>
                    {/* Question Text */}
                    <Grid item xs sx={{ paddingX: 1, zIndex: 1, color: isVideo ? "#fff" : "#000" }}>
                        <Grid
                            container
                            sx={{ height: "100%" }}
                            alignItems="center"
                            justifyContent="center">
                            <Fade in={!isVideo || hasVideoEnded} timeout={1000}>
                                <Grid item>
                                    {poll.questionText.map((each, index) => {
                                        return (
                                            <Typography
                                                key={index}
                                                variant="h6"
                                                fontWeight="normal"
                                                align="center"
                                                sx={{
                                                    lineHeight: 1.25,
                                                    ...(window.innerHeight < 550 && {
                                                        fontSize: "0.8rem",
                                                        lineHeight: 1.2,
                                                    }),
                                                }}>
                                                {each}
                                            </Typography>
                                        );
                                    })}
                                </Grid>
                            </Fade>
                        </Grid>
                    </Grid>

                    {/* Options */}
                    <Grid
                        item
                        id="poll-option-grid"
                        sx={{
                            width: "100%",
                            position: "relative",
                            paddingX: hGap ? `${hGap}px` : undefined,
                            transition: "padding 400ms linear",
                        }}>
                        {isVideo && (
                            <Fab
                                onClick={onVideoButtonClick}
                                className={classes.videoButton}
                                size="small">
                                {hasVideoEnded ? <ReplayRounded /> : <FastForwardRounded />}
                            </Fab>
                        )}
                        <Fade in={!isVideo || hasVideoEnded} timeout={1000}>
                            <Grid
                                container
                                sx={{
                                    p: 1,
                                }}
                                spacing={1}
                                data-v={variant}>
                                {this.options.map((each, optionIndex) => {
                                    const optionResultValue =
                                        totalVotes > 0
                                            ? Math.trunc(((each.totalVote || 0) / totalVotes) * 100)
                                            : 0;
                                    const isResultSelected = selectedOptionId !== undefined;
                                    const hasImage =
                                        Boolean(each.image?.length) && each.image?.[0] !== "";
                                    const hasVideo = Boolean(each.video?.length);
                                    const hasText = Boolean(each.text?.length);
                                    const hasMultilineText = (each.text?.length || 0) > 1;
                                    const isOneByOne = poll.videoPlayOption === "OneByOne";
                                    const muted =
                                        !isOneByOne ||
                                        mutedAudio ||
                                        optionVideoIndex !== optionIndex;

                                    return (
                                        <Grid
                                            key={`${poll.id}-${optionIndex}-${variant}`}
                                            container
                                            item
                                            xs={6}
                                            sx={{
                                                position: "relative",
                                            }}
                                            justifyContent="center">
                                            <Paper
                                                elevation={6}
                                                className={classNames(
                                                    classes.optionPaper,
                                                    {
                                                        [classes.boxShadowNone]:
                                                            (!isShareVariant &&
                                                                selectedOptionId !== undefined) ||
                                                            isVideo,
                                                    },
                                                    {
                                                        [classes.answeredPollOption]:
                                                            !isShareVariant &&
                                                            selectedOptionId !== undefined &&
                                                            each.id !== selectedOptionId,
                                                    },
                                                    {
                                                        [classes.selectedOption]:
                                                            (!isShareVariant &&
                                                                each.id === selectedOptionId) ||
                                                            selectedCategories.includes(each.id),
                                                    }
                                                )}
                                                sx={{
                                                    overflow: "hidden",
                                                    borderRadius: "8px",
                                                    paddingTop:
                                                        reduceSize || poll.pollType === "follow"
                                                            ? "100%"
                                                            : "125%",
                                                    backgroundColor: isVideo
                                                        ? "rgba(0,0,0,0.5)"
                                                        : undefined,
                                                }}
                                                data-option-id={each.id}
                                                data-index={optionIndex}
                                                id={"btn-ans-card-" + each.id}
                                                data-event={
                                                    !!isResultSelected
                                                        ? "result_option_click"
                                                        : mode === "YOURPOLL"
                                                        ? "your_poll_option_click"
                                                        : "poll_option_click"
                                                }
                                                data-question={JSON.stringify(poll.questionText)}
                                                onClick={this.handleOptionClick}>
                                                <Box
                                                    className={classes.optionContent}
                                                    sx={{ padding: hasImage || hasVideo ? 0 : 1 }}>
                                                    <Box
                                                        className={classNames(
                                                            classes.optionResultBg
                                                        )}
                                                        sx={{
                                                            top:
                                                                !isShareVariant &&
                                                                pollType !== "follow" &&
                                                                selectedOptionId !== undefined
                                                                    ? `calc(100% - ${optionResultValue}%)`
                                                                    : `calc(100%)`,
                                                            transition: `top 2s linear`,
                                                        }}
                                                    />

                                                    {each.image?.map((eachImg, index) => {
                                                        if (!eachImg) return undefined;
                                                        return (
                                                            <img
                                                                key={`pi-${poll.id}-${optionIndex}-${index}`}
                                                                className={classNames(
                                                                    classes.optionMedia
                                                                )}
                                                                alt={`Option ${index + 1}`}
                                                                src={
                                                                    this.isDataUrl &&
                                                                    variant === "share" &&
                                                                    !!this.newImage[eachImg]
                                                                        ? this.newImage[eachImg]
                                                                        : getMediaPath(eachImg)
                                                                }
                                                            />
                                                        );
                                                    })}
                                                    {each.video?.map((eachVideo, index) => {
                                                        const poster = each.videoThumb?.[index]
                                                            ? getMediaPath(each.videoThumb?.[index])
                                                            : undefined;
                                                        const video = this.videoRefs[optionIndex]
                                                            .current;
                                                        if (poster && isShareVariant) {
                                                            return (
                                                                <img
                                                                    key={`rbv-${poll.id}-${optionIndex}-${index}`}
                                                                    className={classNames(
                                                                        classes.optionMedia
                                                                    )}
                                                                    alt={`Option ${index + 1}`}
                                                                    src={getMediaPath(poster)}
                                                                />
                                                            );
                                                        }
                                                        return (
                                                            <Grid
                                                                key={`pv-${poll.id}-${optionIndex}-${index}`}
                                                                container
                                                                sx={{ height: "100%" }}
                                                                justifyContent="center">
                                                                <video
                                                                    ref={
                                                                        this.videoRefs[optionIndex]
                                                                    }
                                                                    className={classes.optionMedia}
                                                                    poster={poster}
                                                                    disableRemotePlayback
                                                                    playsInline
                                                                    loop={!isOneByOne}
                                                                    muted={muted}
                                                                    preload="metadata"
                                                                    onLoadedData={
                                                                        this.handleLoadedData
                                                                    }
                                                                    onError={
                                                                        this.handleErrorOnVideo
                                                                    }
                                                                    onEnded={this.handleVideoEnd}
                                                                    onTimeUpdate={
                                                                        this.handleTimeUpdate
                                                                    }>
                                                                    <source
                                                                        type="video/mp4"
                                                                        src={getMediaPath(
                                                                            eachVideo
                                                                        )}
                                                                    />
                                                                </video>
                                                                {isOneByOne &&
                                                                    selectedOptionId ===
                                                                        undefined &&
                                                                    optionIndex ===
                                                                        optionVideoIndex &&
                                                                    !!video &&
                                                                    !video.paused && (
                                                                        <Box
                                                                            className={classNames(
                                                                                classes.progressBar,
                                                                                {
                                                                                    [classes.videoOptionOneByOnePlayProgressBar]:
                                                                                        !isShareVariant &&
                                                                                        !!selectedOptionId,
                                                                                },
                                                                                {
                                                                                    [classes.selectedVideoOption]:
                                                                                        !isShareVariant &&
                                                                                        each.id ===
                                                                                            selectedOptionId,
                                                                                }
                                                                            )}>
                                                                            <LinearProgress
                                                                                variant="determinate"
                                                                                value={
                                                                                    video &&
                                                                                    !video.paused &&
                                                                                    !!video.duration
                                                                                        ? (video.currentTime /
                                                                                              video.duration) *
                                                                                          100
                                                                                        : 0
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                {isOneByOne &&
                                                                    selectedOptionId ===
                                                                        undefined &&
                                                                    (optionIndex !==
                                                                        optionVideoIndex ||
                                                                        !video ||
                                                                        !!video.paused) && (
                                                                        <Grid
                                                                            className={
                                                                                classes.playButtonDiv
                                                                            }
                                                                            sx={{
                                                                                width: "100%",
                                                                                height: "30%",
                                                                            }}
                                                                            id={
                                                                                "btn-poll-play-click" +
                                                                                poll.id
                                                                            }
                                                                            data-event={
                                                                                "option_play_button_click"
                                                                            }
                                                                            data-index={optionIndex}
                                                                            onClick={
                                                                                this.handlePlayClick
                                                                            }>
                                                                            <PlayArrowRounded
                                                                                className={
                                                                                    classes.playButton
                                                                                }
                                                                                data-index={
                                                                                    optionIndex
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                            </Grid>
                                                        );
                                                    })}
                                                    {(((hasImage || hasVideo) && hasText) ||
                                                        (hasVideo && isOneByOne)) && (
                                                        <Box className={classes.gradient} />
                                                    )}
                                                    <Box
                                                        className={classNames(
                                                            classes.textContainer,
                                                            {
                                                                "only-text": pollType === "text",
                                                                "has-media": hasImage || hasVideo,
                                                            }
                                                        )}>
                                                        {each.text?.map((eachText, index) => (
                                                            <Typography
                                                                key={`pt-${poll.id}-${index}`}
                                                                variant="body2"
                                                                sx={{
                                                                    position: "relative",
                                                                    fontSize: "14px",
                                                                    color: isVideo
                                                                        ? "white"
                                                                        : undefined,
                                                                }}
                                                                align="center"
                                                                className={classNames({
                                                                    [classes.textWithMedia]:
                                                                        hasImage || hasVideo,
                                                                })}>
                                                                {hasMultilineText && !eachText ? (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{ lineHeight: "10px" }}>
                                                                        &nbsp;
                                                                    </Box>
                                                                ) : (
                                                                    eachText
                                                                )}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                {/* Follow Poll Tick Icon */}
                                                {pollType === "follow" && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            ...(selectedCategories.includes(
                                                                each.id
                                                            ) && {
                                                                width: "50px",
                                                                height: "50px",
                                                                top: "calc(50% - 25px)",
                                                                left: "calc(50% - 25px)",
                                                                backgroundColor:
                                                                    "rgba(255, 105, 105, 0.55)",
                                                                color: "white",
                                                                border: "2px solid white",
                                                                borderRadius: "50%",
                                                            }),
                                                        }}>
                                                        {selectedCategories.includes(each.id) && (
                                                            <Check
                                                                sx={{
                                                                    height: "30px",
                                                                    width: "30px",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                {/* Percentage Label */}
                                                {pollType !== "follow" && (
                                                    <Box
                                                        className={classNames(
                                                            classes.optionResultValueContainer,
                                                            {
                                                                [classes.optionResultValueContainerFill]:
                                                                    !isShareVariant &&
                                                                    pollType !== "follow" &&
                                                                    selectedOptionId !== undefined,
                                                            }
                                                        )}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontSize: "16px" }}>
                                                            {optionResultValue}
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    lineHeight: "normal",
                                                                }}>
                                                                %
                                                            </Box>
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Fade>
                    </Grid>

                    {/* Placeholder when only two options are given to keep question text in place */}
                    {this.options.length === 2 && (
                        <Grid
                            item
                            sx={{
                                width: "100%",
                                paddingX: hGap ? `${hGap}px` : undefined,
                                transition: "padding 400ms linear",
                            }}>
                            <Grid container sx={{ p: 1 }} spacing={1}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            paddingTop: reduceSize
                                                ? mode === "YOURPOLL"
                                                    ? "75%"
                                                    : "100%"
                                                : mode === "YOURPOLL"
                                                ? "100%"
                                                : "125%",
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* YourPOll Bottom Actions */}
                    {mode === "YOURPOLL" && (
                        <Grid
                            item
                            sx={{
                                zIndex: 1,
                                backgroundColor: "white",
                                borderTop: "1px solid black",
                                p: 1,
                            }}>
                            <Grid container justifyContent="center" alignItems="center">
                                <Grid item>
                                    <ShareButton width={180} poll={poll} mode={mode} />
                                </Grid>
                                {/* Footer */}
                            </Grid>
                        </Grid>
                    )}

                    {/* Bottom Actions */}
                    {mode !== "YOURPOLL" && pollType !== "follow" && (
                        <Collapse
                            in={isVoted}
                            timeout={400}
                            sx={{
                                visibility: hasVideoEnded ? "visible" : "hidden",
                                transitionTimingFunction: "linear",
                            }}>
                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                sx={{
                                    paddingX: 3,
                                    paddingTop: 1,
                                    zIndex: 1,
                                    background: isVideo ? "transparent" : "#fff",
                                }}>
                                <Paper
                                    elevation={0}
                                    onClick={this.handleCommentClick}
                                    className={classes.bottomAction}>
                                    <Badge
                                        badgeContent={commentList.length}
                                        color="error"
                                        invisible={commentList.length > 0 ? false : true}>
                                        <img
                                            className={classes.bottomActionImg}
                                            alt="chatter"
                                            id={"chatter"}
                                            data-event="chatter_icon_click"
                                            src={process.env.PUBLIC_URL + chatter}
                                        />
                                    </Badge>
                                    <Typography
                                        variant="button2"
                                        sx={{
                                            color: isVideo ? "#fff" : colors.brand.blue.main,
                                        }}>
                                        Chatter
                                    </Typography>
                                    <img
                                        className={classes.tickerArrow}
                                        alt="ticker-arrow"
                                        id={"ticker-arrow"}
                                        src={process.env.PUBLIC_URL + tickerArrow}
                                    />
                                </Paper>
                                <Paper
                                    elevation={0}
                                    onClick={() => {
                                        onTrackingChange?.(!isTracking);
                                    }}
                                    className={classes.bottomAction}>
                                    <img
                                        className={classes.bottomActionImg}
                                        alt="track-poll"
                                        id={"track-poll"}
                                        data-event="track_poll_icon_click"
                                        src={process.env.PUBLIC_URL + track}
                                    />
                                    <Typography
                                        variant="button2"
                                        sx={{
                                            color: isVideo ? "#fff" : colors.brand.blue.main,
                                        }}>
                                        {isTracking ? "Tracking Flash" : "Track Flash"}
                                    </Typography>
                                </Paper>
                                <Paper
                                    elevation={0}
                                    onClick={this.handleShareClick}
                                    className={classes.bottomAction}>
                                    <img
                                        className={classes.bottomActionImg}
                                        alt="share"
                                        id={"share"}
                                        data-event="share_icon_click"
                                        src={process.env.PUBLIC_URL + share}
                                    />
                                    <Typography
                                        variant="button2"
                                        sx={{
                                            color: isVideo ? "#fff" : colors.brand.blue.main,
                                        }}>
                                        Share
                                    </Typography>
                                </Paper>
                            </Grid>
                            <div
                                onClick={this.handleCommentClick}
                                style={{
                                    width: availableWidth,
                                    cursor: "pointer",
                                    background: isVideo ? "transparent" : "#fff",
                                }}>
                                <div
                                    style={{
                                        margin: "0px 5px 4px 5px",
                                        padding: "0px 8px 0 8px",
                                        background: isVideo ? "#000" : "#fff",
                                        color: isVideo ? "#fff" : "#000",
                                        borderRadius: "20px",
                                        boxShadow: `0px 0px 2px 0px ${
                                            isVideo ? "#fff" : colors.brand.blue.main
                                        }`,
                                    }}>
                                    <MarqueeSlider speed={100}>
                                        {commentList.length === 0 ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 300,
                                                    my: "4px",
                                                }}>
                                                Be the first to share what you think!
                                            </Box>
                                        ) : (
                                            commentList.map((cmt, idx) => (
                                                <div key={cmt.id} className={classes.tickerItem}>
                                                    <Avatar
                                                        sx={{
                                                            width: 18,
                                                            height: 18,
                                                            marginRight: 1,
                                                        }}
                                                        src={
                                                            cmt.image
                                                                ? `${process.env.REACT_APP_MEDIA_HOST}${cmt.image}`
                                                                : `${process.env.PUBLIC_URL}/images/avatar-placeholder-eb178e7e8ff6fe070b20d117f46a4cc8.webp`
                                                        }
                                                    />
                                                    <Box sx={{ alignItems: "baseline" }}>
                                                        <Box
                                                            component="span"
                                                            sx={{ fontWeight: 500 }}>
                                                            {cmt.name
                                                                ? `${cmt.name}: `
                                                                : getGlobalUserId() === cmt.userId
                                                                ? "You: "
                                                                : `Anonymous ${idx}: `}
                                                        </Box>
                                                        <Box
                                                            sx={{ fontWeight: 300 }}
                                                            component="span">
                                                            {cmt.message}
                                                        </Box>
                                                    </Box>
                                                </div>
                                            ))
                                        )}
                                    </MarqueeSlider>
                                </div>
                            </div>
                        </Collapse>
                    )}
                </Grid>

                {/* Comment Sheet */}
                <CommentDrawer
                    show={this.state.showCommentDrawer}
                    poll={poll}
                    onClose={(updatedComments) => this.handleCommentDrawerClose(updatedComments)}
                />
            </>
        );
    };
}

const Question = withStyles(styles)(Component);

export default connector(Question);
